import { ShoppingCartOutlined } from '@ant-design/icons';
import { Badge, Button } from 'antd';
import ProductList from 'catalog/product/components/product-list.component';
import { TextAlertTypes } from 'config/constants/global.constants';
import { RouteName } from 'config/constants/route.constants';
import { CatalogTranscription, GeneralTranscription, MiniCartTranscription } from 'config/constants/transcription.constant';
import UserService from 'config/services/user.service';
import EmptyMinicart from 'config/static/svg/empty-mini-cart.svg';
import AuthIntentUtils from 'config/utils/auth-intent.util';
import CartUtils from 'config/utils/cart.util';
import GlobalUtils from 'config/utils/globals.util';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Auth } from 'store/models/auth.model';
import { Cart } from 'store/models/cart.model';
import { User } from 'store/models/user.model';
import TextAlert from '../alert.component';
import AppDrawer from '../drawer.component';
import Pulsate from '../pulsate.component';
import SvgIcon from '../svg-icon.component';
import AppTooltip from '../tooltip.component';
import DiscardMiniCart from './discard-mini-cart.component';
import CartSkeleton from './skeleton';
import styles from './style.module.scss';

type MiniCartProps = {
  cart: Cart;
  auth: Auth;
  userProfile: User;
};

const MiniCart: React.FC<MiniCartProps> = ({ cart, userProfile, auth }) => {
  const [isOpenDrawer, setIsOpenDrawer] = useState<boolean>(false);
  const [checkoutTotalFormatted, setCheckoutTotalFormatted] = useState<string>('');
  const [hasTotalError, setHasTotalError] = useState(false);
  const navigate = useNavigate();

  const { items } = cart;
  const someItemOutOfStock = CartUtils.computeSomeItemOutOfStock(items);
  const canGoToCheckout = CartUtils.hasPurchasableItems(items);

  const handleShowDrawer = (openDrawer: boolean) => setIsOpenDrawer(openDrawer);

  const goToCheckout = useCallback(async () => {
    if (!CartUtils.hasPurchasableItems(items)) return;
    handleShowDrawer(false);
    if (!auth.isAuthenticated) {
      AuthIntentUtils.requireAuth({ redirectTo: RouteName.PURCHASE_CHECKOUT });
      return;
    }
    await CartUtils.flushPendingSync();
    navigate(RouteName.PURCHASE_CHECKOUT);
  }, [items, navigate, auth.isAuthenticated]);

  const loadCheckoutTotal = useCallback(async () => {
    const checkout = await UserService.getCheckout({
      items: CartUtils.toCartApiItems(items),
      selectedAddressForCheckout: { stateCode: '', neighborhoodCode: '' },
      isDelivery: false,
      isPickup: true,
    });
    return checkout.totalFormatted || '';
  }, [items]);

  useEffect(() => {
    if (!isOpenDrawer || items.length === 0) {
      setCheckoutTotalFormatted('');
      setHasTotalError(false);
      return;
    }
    let cancelled = false;
    setCheckoutTotalFormatted('');
    setHasTotalError(false);
    loadCheckoutTotal()
      .then((total) => {
        if (cancelled) return;
        if (total) setCheckoutTotalFormatted(total);
        else setHasTotalError(true);
      })
      .catch(() => {
        if (!cancelled) setHasTotalError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpenDrawer, loadCheckoutTotal]);

  const renderFooter = useMemo<React.ReactNode>(() => {
    if (items.length === 0) return null;

    if (someItemOutOfStock) {
      return <TextAlert text={CatalogTranscription.SOME_ITEMS_OUT_OF_STOCK} type={TextAlertTypes.WARNING} showIcon />;
    }

    const showTotalSkeleton = isOpenDrawer && !checkoutTotalFormatted && !hasTotalError;
    const totalText = GlobalUtils.replaceTranslateKey(GeneralTranscription.TOTAL, 'amount', checkoutTotalFormatted);
    const totalLabel = GlobalUtils.replaceTranslateKey(GeneralTranscription.TOTAL, 'amount', '');

    return (
      <div className={styles['drawer-footer-container']}>
        <div className={styles['description']}>
          {showTotalSkeleton ?
            <React.Fragment>
              {totalLabel}
              <Pulsate width='88px' height='16px' marginBottom='0' />
            </React.Fragment>
          : totalText}
        </div>
        <div className={styles['actions']}>
          <Button type='link' onClick={() => handleShowDrawer(false)}>
            {GeneralTranscription.CANCEL}
          </Button>
          <Button type='primary' onClick={goToCheckout} disabled={!canGoToCheckout || showTotalSkeleton}>
            {GeneralTranscription.CONTINUE}
          </Button>
        </div>
      </div>
    );
  }, [items, someItemOutOfStock, canGoToCheckout, goToCheckout, checkoutTotalFormatted, hasTotalError, isOpenDrawer]);

  const handleDiscardCart = useCallback(async () => {
    setIsOpenDrawer(false);
    await CartUtils.discardCart();
  }, []);

  return (
    <React.Fragment>
      <div className={styles['mini-cart-container']} onClick={() => handleShowDrawer(true)}>
        <AppTooltip title={MiniCartTranscription.MINI_CART_TOOLTIP_TITLE} placement='bottomLeft' arrow>
          <Badge size='small' count={items.length}>
            <ShoppingCartOutlined className={styles['mini-cart-icon']} />
          </Badge>
        </AppTooltip>
      </div>
      <AppDrawer
        title={MiniCartTranscription.MINI_CART_DRAWER_TITLE}
        isOpen={isOpenDrawer}
        onClose={() => handleShowDrawer(false)}
        footer={renderFooter}
      >
        {items.length === 0 ?
          <div className={styles['empty-cart-container']}>
            <SvgIcon source={EmptyMinicart} name='current-address-icon' height={300} width={400} />
            <p>{CatalogTranscription.EMPTY_CART_TEXT}</p>
          </div>
        : cart.isLoading ?
          <CartSkeleton />
        : <div className={styles['mini-cart-body']}>
            <ProductList items={items} isAuthenticated={auth.isAuthenticated} withControls userProfile={userProfile} />
            <div className={styles['discard-cart-action']}>
              <DiscardMiniCart onConfirm={handleDiscardCart} />
            </div>
          </div>
        }
      </AppDrawer>
    </React.Fragment>
  );
};

export default MiniCart;
